import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../css/InvoicePopover.css';

const InvoiceModal = ({ invoices, onViewInvoice, onClose }) => {
    const modalRef = useRef(null);

    // Close on escape
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Parse amount helper - handles string amounts like "$123.45"
    const parseAmount = (amt) => {
        if (!amt || amt === 'Not Found') return 0;
        if (typeof amt === 'number') return amt;
        return parseFloat(String(amt).replace(/[^0-9.-]/g, '')) || 0;
    };

    // Detect potential duplicates
    const findDuplicates = (invoices) => {
        const duplicateGroups = {};

        invoices.forEach((invoice, index) => {
            // Create a key based on amount and vendor (if available)
            const amount = parseAmount(invoice.totalAmount || invoice.amount);
            const vendor = (invoice.vendor || invoice.payee || '').toLowerCase().trim();

            // Check against all other invoices
            invoices.forEach((other, otherIndex) => {
                if (index >= otherIndex) return; // Avoid comparing same or already compared

                const otherAmount = parseAmount(other.totalAmount || other.amount);
                const otherVendor = (other.vendor || other.payee || '').toLowerCase().trim();

                // Check if potentially duplicate
                const sameAmount = Math.abs(amount - otherAmount) < 0.01;
                const sameVendor = vendor && otherVendor && vendor === otherVendor;
                const similarVendor = vendor && otherVendor && (
                    vendor.includes(otherVendor) || otherVendor.includes(vendor)
                );

                if (sameAmount && (sameVendor || similarVendor)) {
                    // Mark both as potential duplicates
                    duplicateGroups[invoice.id] = true;
                    duplicateGroups[other.id] = true;
                }
            });
        });

        return duplicateGroups;
    };

    const duplicates = findDuplicates(invoices);
    const hasDuplicates = Object.keys(duplicates).length > 0;

    // Format currency - handles both string amounts like "$123.45" and numbers
    const formatCurrency = (amount) => {
        if (!amount || amount === 'Not Found') return 'N/A';

        // If it's already a formatted string with $, return as-is
        if (typeof amount === 'string' && amount.includes('$')) {
            return amount;
        }

        // Try to parse as number
        const num = typeof amount === 'string'
            ? parseFloat(amount.replace(/[^0-9.-]/g, ''))
            : amount;

        if (isNaN(num)) return amount || 'N/A';

        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(num);
    };

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    // Get status badge
    const getStatusBadge = (invoice) => {
        if (invoice.isReviewed) {
            return <span className="invoice-status-badge reviewed">Reviewed</span>;
        }
        if (invoice.status) {
            const statusClass = invoice.status.toLowerCase().replace(/\s+/g, '-');
            return <span className={`invoice-status-badge ${statusClass}`}>{invoice.status}</span>;
        }
        return <span className="invoice-status-badge pending">Pending</span>;
    };

    // Use Portal to render modal outside of parent DOM hierarchy
    return ReactDOM.createPortal(
        <div className="invoice-modal-overlay" onClick={onClose}>
            <div className="invoice-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
                <div className="invoice-modal-header">
                    <h4>
                        <span className="header-icon">💰</span>
                        Select Invoice ({invoices.length})
                    </h4>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>

                {hasDuplicates && (
                    <div className="duplicate-warning">
                        <span className="warning-icon">⚠️</span>
                        <span>Potential duplicates detected</span>
                    </div>
                )}

                <div className="invoice-modal-list">
                    {invoices.map((invoice, index) => {
                        const isDuplicate = duplicates[invoice.id];
                        const amount = invoice.totalAmount || invoice.amount;
                        const vendor = invoice.vendor || invoice.payee || 'Unknown Vendor';
                        const date = invoice.invoiceDate || invoice.date || invoice.createdAt;

                        return (
                            <div
                                key={invoice.id || index}
                                className={`invoice-modal-item ${isDuplicate ? 'is-duplicate' : ''}`}
                                onClick={() => {
                                    onViewInvoice(invoice.id);
                                    onClose();
                                }}
                            >
                                <div className="invoice-item-main">
                                    <div className="invoice-item-left">
                                        <span className="invoice-index">{index + 1}</span>
                                        <div className="invoice-item-info">
                                            <div className="invoice-vendor">{vendor}</div>
                                            <div className="invoice-meta">
                                                {invoice.invoiceNumber && (
                                                    <span className="invoice-number">#{invoice.invoiceNumber}</span>
                                                )}
                                                {date && (
                                                    <span className="invoice-date">{formatDate(date)}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="invoice-item-right">
                                        <div className="invoice-amount">{formatCurrency(amount)}</div>
                                        {getStatusBadge(invoice)}
                                    </div>
                                </div>

                                {isDuplicate && (
                                    <div className="duplicate-indicator">
                                        <span className="duplicate-icon">⚠️</span>
                                        <span>Possible duplicate</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="invoice-modal-footer">
                    <span className="footer-hint">Click an invoice to view details</span>
                </div>
            </div>
        </div>,
        document.body
    );
};

// Wrapper component that handles the badge click and modal state
const InvoiceBadgeWithPopover = ({ invoices, onViewInvoice }) => {
    const [showModal, setShowModal] = useState(false);

    if (!invoices || invoices.length === 0) return null;

    // Check for duplicates to show warning indicator on badge
    const hasPotentialDuplicates = () => {
        if (invoices.length < 2) return false;

        for (let i = 0; i < invoices.length; i++) {
            for (let j = i + 1; j < invoices.length; j++) {
                const a = invoices[i];
                const b = invoices[j];

                // Parse amounts - handle string amounts like "$123.45"
                const parseAmount = (amt) => {
                    if (!amt || amt === 'Not Found') return 0;
                    if (typeof amt === 'number') return amt;
                    return parseFloat(String(amt).replace(/[^0-9.-]/g, '')) || 0;
                };

                const amountA = parseAmount(a.totalAmount || a.amount);
                const amountB = parseAmount(b.totalAmount || b.amount);

                if (Math.abs(amountA - amountB) < 0.01) {
                    return true;
                }
            }
        }
        return false;
    };

    const handleBadgeClick = (e) => {
        e.stopPropagation();
        e.preventDefault();

        // If only one invoice, go directly to it
        if (invoices.length === 1) {
            onViewInvoice(invoices[0].id);
            return;
        }

        // Open modal for multiple invoices
        setShowModal(true);
    };

    const showDuplicateWarning = hasPotentialDuplicates();

    return (
        <div className="invoice-badge-container">
            <button
                className={`invoice-badge ${showDuplicateWarning ? 'has-warning' : ''}`}
                onClick={handleBadgeClick}
                title={invoices.length === 1 ? 'Click to view invoice' : `Click to select from ${invoices.length} invoices`}
            >
                <span className="invoice-badge-icon">💰</span>
                <span className="invoice-badge-text">
                    {invoices.length === 1 ? 'Invoice' : `${invoices.length} Invoices`}
                </span>
                {showDuplicateWarning && (
                    <span className="badge-warning-dot" title="Possible duplicates">!</span>
                )}
            </button>

            {showModal && (
                <InvoiceModal
                    invoices={invoices}
                    onViewInvoice={onViewInvoice}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
};

export default InvoiceBadgeWithPopover;
